import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useItemsStore, useSessionStore, useTenantsStore, useTradeItemsStore, useSupplierItemsStore } from "@/stores";
import type ISupplier from "@/models/responses/ISupplier";
import UpsertSupplierRequest from "@/models/requests/UpsertSupplierRequest";
import ISupplierWithItems from "@/models/responses/ISupplierWithItems";
import Supplier from "@/models/frontend/Supplier";
import SupplierWithItems from "@/models/frontend/SupplierWithItems";
import SupplierItem from "@/models/frontend/SupplierItem";

export const useSuppliersStore = defineStore("supplier", {
  state: () => ({
    error: "" as any,
    data: [] as Supplier[],
    loadedTenantId: 0,
  }),
  actions: {
    async fetchAll(force = false) {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();

      const { isAuthenticated } = storeToRefs(authStore);
      const { selectedTenantId } = storeToRefs(sessionStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loadedTenantId === selectedTenantId.value) return;

      this.error = "";
      await api
        .get<ISupplier[]>(`v1.0/suppliers?tenantId=${selectedTenantId.value}`)
        .then((response) => {
          this.data = response.data.map((supplier) => this.mapToFrontend(supplier));
          this.loadedTenantId = selectedTenantId.value;
        })
        .catch((error) => (this.error = error));
    },
    async fetch(id: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .get<ISupplier>(`v1.0/suppliers/${id}`)
        .then((response) => {
          const index = this.data.findIndex((x) => x.id === id);
          const mappedSupplier = this.mapToFrontend(response.data);
          if (index === -1) this.data.push(mappedSupplier);
          else this.data[index] = mappedSupplier;
        })
        .catch((error) => (this.error = error));
    },
    async upsert(supplier: UpsertSupplierRequest, onfulfilled: (data: SupplierWithItems) => PromiseLike<void>, onrejected: (reason: any) => void | PromiseLike<void>) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .post<ISupplierWithItems>(`v1.0/suppliers`, supplier)
        .then(async (response) => {
          const index = this.data.findIndex((x) => x.id === response.data.id);
          const mappedSupplier = this.mapWitItemsToFrontend(response.data);
          if (index === -1) this.data.push(mappedSupplier);
          else this.data[index] = mappedSupplier;

          const supplierItemsStore = useSupplierItemsStore();
          supplierItemsStore.setSupplierItems(mappedSupplier);

          await onfulfilled(mappedSupplier);
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    async delete(id: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .delete(`v1.0/suppliers/${id}`)
        .then(() => {
          const index = this.data.findIndex((x) => x.id === id);
          if (index !== -1) this.data.splice(index, 1);
        })
        .catch((error) => (this.error = error));
    },
    mapToFrontend(supplier: ISupplier): Supplier {
      const tenant = useTenantsStore().getById(supplier.tenantId);
      return new Supplier(
        supplier.id,
        tenant,
        supplier.code,
        supplier.name,
        supplier.email,
        supplier.customerCode,
        supplier.info,
      );
    },
    mapWitItemsToFrontend(supplier: ISupplierWithItems): SupplierWithItems {
      const itemsStore = useItemsStore();
      const tradeItemsStore = useTradeItemsStore();
      const tenant = useTenantsStore().getById(supplier.tenantId);
      const tmpSupplier = new Supplier(
        supplier.id,
        tenant,
        supplier.code,
        supplier.name,
        supplier.email,
        supplier.customerCode,
        supplier.info,
      );

      const supplierItems = supplier.supplierItems.map((supplierItem) => {
        const item = itemsStore.getById(supplierItem.itemId);
        const tradeItem = tradeItemsStore.getByItemIdAndUnitCode(supplierItem.itemId, supplierItem.orderUnitCode);
        if (tradeItem === undefined) {
          return new SupplierItem(supplierItem.id, tmpSupplier, item, supplierItem.code, supplierItem.orderUnitCode, 1, supplierItem.orderUnitCode, supplierItem.name, supplierItem.description, supplierItem.proposedOrderQuantity);
        }
        else {
          return new SupplierItem(supplierItem.id, tmpSupplier, item, supplierItem.code, supplierItem.orderUnitCode, tradeItem.factor, item.baseUnitCode, supplierItem.name, supplierItem.description, supplierItem.proposedOrderQuantity);
        }
      });
      return new SupplierWithItems(
        supplier.id,
        tenant,
        supplier.code,
        supplier.name,
        supplier.email,
        supplier.customerCode,
        supplier.info,
        supplierItems,
      );
    },
  },
  getters: {
    getById:
      (state) =>
      (id: number): Supplier => {
        return <Supplier>(
          (state.data as Supplier[]).find((supplier) => supplier.id === id)
        );
      },
    getByCode:
      (state) =>
      (code: string): Supplier => {
        return <Supplier>(
          (state.data as Supplier[]).find((supplier) => supplier.code === code)
        );
      },
  },
});
